import { useEffect } from 'react'

import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node'
import {
  json,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  type MetaFunction,
} from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { useChangeLanguage } from 'remix-i18next/react'
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'

import { GeneralErrorBoundary } from './components/errors.tsx'
import { GtmScript } from './components/gtm.tsx'
import { Navbar } from './components/navbar.tsx'
import tailwindStylesheetUrl from './styles/tailwind.css?url'
import type { Handle } from './types'
import { csrf } from './utils/csrf.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { getLocaleFromRequest } from './utils/i18n.ts'
import { mapLink } from './utils/mappers.ts'
import { combineHeaders, getDomainUrl, getUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getSocialMetas } from './utils/seo.ts'
import { getNavigation } from './utils/strapi.ts'

export const links: LinksFunction = () => [
  {
    rel: 'apple-touch-icon',
    sizes: '180x180',
    href: '/favicons/apple-touch-icon.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
    href: '/favicons/favicon-32x32.png',
  },
  {
    rel: 'icon',
    type: 'image/png',
    sizes: '16x16',
    href: '/favicons/favicon-16x16.png',
  },
  { rel: 'manifest', href: '/site.webmanifest' },
  { rel: 'icon', href: '/favicon.ico' },
  { rel: 'stylesheet', href: tailwindStylesheetUrl },
]

export const handle: Handle = {
  i18n: 'common',
}

export type RootLoaderType = typeof loader

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = getLocaleFromRequest(request)
  const navigation = await getNavigation(locale)

  const honeyProps = honeypot.getInputProps()
  const [csrfToken, csrfCookieHeader] = await csrf.commitToken()

  return json(
    {
      locale,
      honeyProps,
      csrfToken,
      ENV: getEnv(),
      navigation:
        navigation?.items?.map(mapLink).sort(item => (item.url ? -1 : 1)) ?? [],
      requestInfo: {
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
      },
    },
    {
      headers: combineHeaders(
        csrfCookieHeader ? { 'set-cookie': csrfCookieHeader } : null,
      ),
    },
  )
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const requestInfo = data?.requestInfo
  return [
    ...getSocialMetas({
      title: 'V12',
      description: 'V12: Restaurant, Bistro, Bar.',
      url: getUrl(requestInfo),
    }),
  ]
}

function Document({
  children,
  nonce,
  locale,
  env,
}: {
  children: React.ReactNode
  nonce: string
  locale?: string
  env?: Record<string, string>
}) {
  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <GtmScript gtmId={ENV.GTM_ID ?? ''} nonce={nonce ?? ''} />
      </head>
      <body>
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <script
          id="tebi"
          nonce={nonce}
          src="https://live.tebi.co/ecom/widget-manager.js"
          data-widget-token={ENV.TEBI_WIDGET_TOKEN}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  )
}

function App() {
  const data = useLoaderData<typeof loader>()
  const nonce = useNonce()
  const location = useLocation()

  useChangeLanguage(data.locale)

  const isHomePage = location.pathname === '/' || location.pathname === '/en'

  useEffect(() => {
    if (window?.dataLayer) {
      window.dataLayer.push({
        event: 'pageview',
        pagePath: location.pathname + location.search,
        pageTitle: document.title,
      })
    }
  }, [location])

  return (
    <Document locale={data.locale} nonce={nonce} env={data.ENV}>
      <Navbar hideLogo={isHomePage} />
      <Outlet />
    </Document>
  )
}

export default function AppWithProviders() {
  const data = useLoaderData<typeof loader>()
  return (
    <AuthenticityTokenProvider token={data.csrfToken}>
      <HoneypotProvider {...data.honeyProps}>
        <App />
      </HoneypotProvider>
    </AuthenticityTokenProvider>
  )
}

export function ErrorBoundary() {
  const nonce = useNonce()
  const { i18n } = useTranslation()

  return (
    <Document nonce={nonce} locale={i18n.language ?? 'nl'}>
      <GeneralErrorBoundary />
    </Document>
  )
}
